import { useMemo, useCallback, useEffect } from "react";
import { DateTime, Interval } from "luxon";
import { Box } from "@chakra-ui/react";
import { defineMessages, useIntl } from "react-intl";
import { FilterContainer } from "../FilterContainer";
import { dateToMinutes, generateHourMarkers, getDefaultSelection, getEarliestAndLatest, getDefaultDayIntervalsSelection, isValidInterval, ValidInterval, formatTime } from "./timePickerUtils";
import { TimePickerHeader } from "./TimePickerHeader";
import { TickLabels, TickLines } from "./TickMarkers";
import { IntervalSegments, OverlaySegment } from "./IntervalSegments";
import { SliderThumb } from "./SliderThumb";
import { useSliderDrag } from "./useSliderDrag";
import { MINUTES_PER_HOUR, MINUTES_PER_DAY } from "./timePickerConstants";
const messages = defineMessages({
  startTime: {
    id: "timePicker.startTime",
    defaultMessage: "Start time"
  },
  endTime: {
    id: "timePicker.endTime",
    defaultMessage: "End time"
  }
});
export interface TimeFilterProps {
  timeIntervals: Interval[];
  onApplyRange: (start: DateTime, end: DateTime) => void;
  onDraftChange?: (start: DateTime, end: DateTime) => void;
  selectedStart?: DateTime | null;
  selectedEnd?: DateTime | null;
}
export const TimeFilter = ({
  timeIntervals,
  onApplyRange,
  onDraftChange,
  selectedStart: initialStart,
  selectedEnd: initialEnd
}: TimeFilterProps) => {
  const intl = useIntl();
  const validIntervals = useMemo(() => timeIntervals.filter(isValidInterval), [timeIntervals]);
  const intervalsByDate = useMemo(() => {
    const grouped: Record<string, ValidInterval[]> = {};
    for (const interval of validIntervals) {
      const key = interval.start.toISODate();
      (grouped[key] ??= []).push(interval);
    }
    return grouped;
  }, [validIntervals]);
  const dateKeys = useMemo(() => Object.keys(intervalsByDate).sort((a, b) => a.localeCompare(b)), [intervalsByDate]);
  const {
    trackRef,
    draftStart,
    draftEnd,
    setDraftStart,
    setDraftEnd,
    dragConfigRef,
    handleThumbPointerDown
  } = useSliderDrag();
  useEffect(() => {
    if (initialStart && initialEnd) {
      setDraftStart(initialStart);
      setDraftEnd(initialEnd);
      return;
    }
    const bounds = getDefaultDayIntervalsSelection(validIntervals);
    if (!bounds) return;
    setDraftStart(bounds.start);
    setDraftEnd(bounds.end);
  }, [validIntervals, initialStart, initialEnd, setDraftStart, setDraftEnd]);
  useEffect(() => {
    if (draftStart && draftEnd) onDraftChange?.(draftStart, draftEnd);
  }, [draftStart, draftEnd, onDraftChange]);
  const startTimestamp = draftStart;
  const endTimestamp = draftEnd;
  const selectedDateKey = useMemo(() => {
    const startKey = startTimestamp?.toISODate();
    if (startKey && dateKeys.includes(startKey)) return startKey;
    return dateKeys[0] ?? "";
  }, [startTimestamp, dateKeys]);
  const intervals = useMemo(() => (intervalsByDate[selectedDateKey] ?? []).sort((a_0, b_0) => a_0.start.valueOf() - b_0.start.valueOf()), [intervalsByDate, selectedDateKey]);
  const dayStartMinutes = useMemo(() => {
    if (intervals.length === 0) return 0;
    return Math.floor(dateToMinutes(intervals[0].start) / MINUTES_PER_HOUR) * MINUTES_PER_HOUR;
  }, [intervals]);
  const dayEndMinutes = useMemo(() => {
    if (intervals.length === 0) return MINUTES_PER_DAY;
    return Math.ceil(dateToMinutes(intervals[intervals.length - 1].end) / MINUTES_PER_HOUR) * MINUTES_PER_HOUR;
  }, [intervals]);
  const startMinutes = startTimestamp ? dateToMinutes(startTimestamp) : 0;
  const endMinutes = endTimestamp ? dateToMinutes(endTimestamp) : 0;
  const totalRange = dayEndMinutes - dayStartMinutes;
  const activeBaseDate = useMemo(() => intervals[0]?.start ?? DateTime.now(), [intervals]);

  // Keep drag handler config in sync with latest computed values.
  dragConfigRef.current = {
    dayStartMinutes,
    totalRange,
    baseDate: activeBaseDate,
    startMinutes,
    endMinutes
  };
  const hourMarkers = useMemo(() => generateHourMarkers(dayStartMinutes, dayEndMinutes), [dayStartMinutes, dayEndMinutes]);
  const intervalOverlays = useMemo(() => {
    if (totalRange <= 0) return [];
    return intervals.map(interval_0 => {
      const startPct = (dateToMinutes(interval_0.start) - dayStartMinutes) / totalRange * 100;
      const endPct = (dateToMinutes(interval_0.end) - dayStartMinutes) / totalRange * 100;
      return {
        startPercent: startPct,
        widthPercent: endPct - startPct
      };
    });
  }, [intervals, dayStartMinutes, totalRange]);
  const selectedOverlays = useMemo(() => {
    if (totalRange <= 0) return [];
    return intervals.map(interval_1 => {
      const intervalStart = dateToMinutes(interval_1.start);
      const intervalEnd = dateToMinutes(interval_1.end);
      const clampedStart = Math.max(intervalStart, startMinutes);
      const clampedEnd = Math.min(intervalEnd, endMinutes);
      if (clampedStart >= clampedEnd) return null;
      const startPct_0 = (clampedStart - dayStartMinutes) / totalRange * 100;
      const endPct_0 = (clampedEnd - dayStartMinutes) / totalRange * 100;
      return {
        startPercent: startPct_0,
        widthPercent: endPct_0 - startPct_0
      };
    }).filter(Boolean) as OverlaySegment[];
  }, [intervals, dayStartMinutes, totalRange, startMinutes, endMinutes]);
  const hasDataInRange = useMemo(() => intervals.some(interval_2 => {
    const intervalStart_0 = dateToMinutes(interval_2.start);
    const intervalEnd_0 = dateToMinutes(interval_2.end);
    return intervalStart_0 <= endMinutes && intervalEnd_0 >= startMinutes;
  }), [intervals, startMinutes, endMinutes]);
  const minutesToTrackPercent = useCallback((minutes: number): number => {
    if (totalRange <= 0) return 0;
    return (minutes - dayStartMinutes) / totalRange * 100;
  }, [dayStartMinutes, totalRange]);
  const handleDateChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const newDateKey = e.target.value;
    const newIntervals = intervalsByDate[newDateKey];
    if (!newIntervals || newIntervals.length === 0) return;
    const available = getEarliestAndLatest(newIntervals);
    if (!available) return;
    const bounds_0 = getDefaultSelection(available);
    setDraftStart(bounds_0.start);
    setDraftEnd(bounds_0.end);
  }, [intervalsByDate]);
  const handleReset = useCallback(() => {
    const available_0 = getEarliestAndLatest(intervals);
    if (!available_0) return;
    const bounds_1 = getDefaultSelection(available_0);
    setDraftStart(bounds_1.start);
    setDraftEnd(bounds_1.end);
  }, [intervals]);
  const startPct_1 = minutesToTrackPercent(startMinutes);
  const endPct_1 = minutesToTrackPercent(endMinutes);
  return <FilterContainer data-testid="time-picker-card">
      <TimePickerHeader dateKeys={dateKeys} selectedDateKey={selectedDateKey} hasDataInRange={hasDataInRange} onDateChange={handleDateChange} onApply={() => {
      if (startTimestamp && endTimestamp) onApplyRange(startTimestamp, endTimestamp);
    }} onReset={handleReset} />

      <Box data-testid="time-picker-slider" mt="4" position="relative" role="group" aria-label="Time range slider" bg="grey.100" borderRadius="button" px="6" pt="4" pb="6">
        {totalRange > 0 && <TickLabels hourMarkers={hourMarkers} minutesToTrackPercent={minutesToTrackPercent} />}

        <Box position="relative" height="2.25rem">
          {totalRange > 0 && <TickLines hourMarkers={hourMarkers} minutesToTrackPercent={minutesToTrackPercent} />}

          <Box ref={trackRef} data-testid="time-picker-track" position="absolute" top="0" left="0" right="0" bottom="0" bg="transparent" borderRadius="full">
            <IntervalSegments intervalOverlays={intervalOverlays} selectedOverlays={selectedOverlays} />

            <SliderThumb testId="time-picker-thumb-start" label={intl.formatMessage(messages.startTime)} valueMin={dayStartMinutes} valueMax={dayEndMinutes} valueNow={startMinutes} valueText={formatTime(startMinutes)} leftPercent={startPct_1} onPointerDown={handleThumbPointerDown("start")} />

            <SliderThumb testId="time-picker-thumb-end" label={intl.formatMessage(messages.endTime)} valueMin={dayStartMinutes} valueMax={dayEndMinutes} valueNow={endMinutes} valueText={formatTime(endMinutes)} leftPercent={endPct_1} onPointerDown={handleThumbPointerDown("end")} />
          </Box>
        </Box>
      </Box>
    </FilterContainer>;
};